import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge, { StageBadge } from "@/components/StatusBadge";
import PaymentBadge from "@/components/PaymentBadge";
import { useStatuses } from "@/context/StatusContext";
import ShipmentForm from "@/components/ShipmentForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { buildWaMessage, groupByCourier } from "@/lib/whatsapp";
import StatusTimeline from "@/components/StatusTimeline";
import ReshipDialog from "@/components/ReshipDialog";
import ReversePickupDialog from "@/components/ReversePickupDialog";
import TriggerChooserDialog from "@/components/TriggerChooserDialog";
import LinkedCleanupDialog from "@/components/LinkedCleanupDialog";
import DirectionBadge from "@/components/DirectionBadge";
import MultiSelect from "@/components/MultiSelect";
import SavedViews from "@/components/SavedViews";
import { useReship } from "@/context/ReshipContext";
import { useRvp } from "@/context/RvpContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, Upload, ExternalLink, MoreVertical, Pencil, Trash2, RefreshCw, Search, AlertTriangle,
  History, MessageCircle, CircleSlash, Users2, Download, FileSpreadsheet, Loader2, Undo2, Phone,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }); }
  catch { return "—"; }
}

export default function Shipments() {
  const { isAdmin, user } = useAuth();
  const { refresh: refreshReship } = useReship();
  const { refresh: refreshRvp } = useRvp();
  const { statuses, metaMap, stages, stageMap } = useStatuses();
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [status, setStatus] = useState([]);
  const [stage, setStage] = useState([]);
  const [courier, setCourier] = useState([]);
  const [payment, setPayment] = useState([]);
  const [overdue, setOverdue] = useState(false);
  const [unresolved, setUnresolved] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [direction, setDirection] = useState("all");
  const [historyFor, setHistoryFor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [waPrompt, setWaPrompt] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reshipFor, setReshipFor] = useState(null);
  const [rvpFor, setRvpFor] = useState(null);
  const [chooserFor, setChooserFor] = useState(null);
  const [chainReshipFor, setChainReshipFor] = useState(null);
  const [chainRvpFor, setChainRvpFor] = useState(null);
  const [cleanupFor, setCleanupFor] = useState(null);
  const [pending, setPending] = useState([]);
  const loadPending = () => api.get("/pending-reshipments").then((r) => setPending(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const view = searchParams.get("view");
    if (!view) return;
    setStatus([]); setOverdue(false); setUnresolved(false);
    if (view === "open") setUnresolved(true);
    else if (view === "resolved") setStage(["resolved"]);
    else if (view === "overdue") setOverdue(true);
    else if (view === "delivered") setStatus(["delivered"]);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (overdue) params.overdue = true;
    if (search) params.search = search;
    try {
      const { data } = await api.get("/shipments", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [overdue, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/shipments/couriers").then((r) => setCouriers(r.data)).catch(() => {});
    api.get("/team").then((r) => setAgents(r.data)).catch(() => {});
  }, []);

  const applyStatusRow = async (id, newStatus) => {
    try {
      await api.put(`/shipments/${id}`, { status: newStatus });
      load();
      setChainReshipFor(null);
      setChainRvpFor(null);
      const meta = metaMap?.[newStatus] || {};
      const s = items.find((x) => x.id === id);
      if (s && meta.triggers_rvp && meta.triggers_reship) { loadPending(); refreshReship(); refreshRvp(); setChooserFor(s); return; }
      if (s && meta.triggers_rvp) { refreshRvp(); setRvpFor(s); toast.info("Reverse pickup requested — add details or skip for now"); return; }
      if (s && meta.triggers_reship) { loadPending(); refreshReship(); setReshipFor(s); toast.info("New shipment requested — add details or skip for now"); return; }
      toast.success("Final action updated");
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const changeStatus = async (id, newStatus) => {
    const meta = metaMap?.[newStatus] || {};
    const s = items.find((x) => x.id === id);
    const dropReship = !!(s && (s.reshipped || s.reship_pending)) && !meta.triggers_reship;
    const dropRvp = !!(s && s.is_reverse_pickup) && !meta.triggers_rvp;
    if (dropReship || dropRvp) { setCleanupFor({ id, status: newStatus, orderId: s?.order_id, dropReship, dropRvp }); return; }
    applyStatusRow(id, newStatus);
  };

  const confirmCleanupRow = async (doDelete) => {
    const c = cleanupFor;
    setCleanupFor(null);
    if (!c) return;
    if (doDelete) {
      if (c.dropReship) await api.post(`/shipments/${c.id}/reship/cancel`).catch(() => {});
      if (c.dropRvp) await api.post(`/shipments/${c.id}/reverse-pickup/remove`).catch(() => {});
      loadPending(); refreshReship(); refreshRvp();
      toast.success("Linked follow-up deleted");
    }
    applyStatusRow(c.id, c.status);
  };

  const handleChooseRow = (choice) => {
    const s = chooserFor;
    setChooserFor(null);
    if (!s) return;
    if (choice === "rvp_first") { setChainReshipFor(s); setRvpFor(s); toast.info("Reverse pickup first — new shipment opens next"); }
    else if (choice === "reship_first") { setChainRvpFor(s); setReshipFor(s); toast.info("New shipment first — reverse pickup opens next"); }
    else { toast.info("Both kept pending — find them in the Reverse Pickup & New Shipments queues"); }
  };

  const changeStage = async (id, v) => {
    try {
      await api.put(`/shipments/${id}`, { stage: v });
      load();
      if (stageMap[v]?.triggers_reship) {
        const s = items.find((x) => x.id === id);
        loadPending();
        refreshReship();
        if (s) { setReshipFor(s); toast.info("New shipment requested — add details or skip for now"); return; }
      }
      toast.success("Status updated");
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this shipment?")) return;
    await api.delete(`/shipments/${id}`);
    toast.success("Shipment deleted");
    load();
    loadPending();
    refreshReship();
  };

  const overdueCount = useMemo(() => items.filter((i) => i.is_overdue).length, [items]);
  const rows = useMemo(() => {
    let r = items;
    if (direction !== "all") r = r.filter((i) => (i.direction || "forward") === direction);
    if (unresolved) r = r.filter((i) => !i.resolved);
    if (mineOnly) r = r.filter((i) => i.assigned_to === user?.id);
    if (status.length) r = r.filter((i) => status.includes(i.status));
    if (stage.length) r = r.filter((i) => stage.includes(i.stage || "open"));
    if (courier.length) r = r.filter((i) => courier.includes(i.courier_partner));
    if (payment.length) r = r.filter((i) => payment.includes(i.payment_method));
    return r;
  }, [items, direction, unresolved, mineOnly, status, stage, courier, payment, user]);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pagedRows = useMemo(() => rows.slice((currentPage - 1) * pageSize, currentPage * pageSize), [rows, currentPage, pageSize]);
  useEffect(() => { setPage(1); }, [direction, unresolved, mineOnly, stage, status, courier, overdue, search, pageSize]);
  const selectedRows = useMemo(() => rows.filter((r) => selected[r.id]), [rows, selected]);
  const allChecked = rows.length > 0 && selectedRows.length === rows.length;
  const toggleAll = () => allChecked ? setSelected({}) : setSelected(Object.fromEntries(rows.map((r) => [r.id, true])));
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const whatsapp = () => {
    const target = selectedRows.length ? selectedRows : rows;
    if (!target.length) { toast.error("No shipments to send"); return; }
    setWaPrompt(target);
  };

  const exportCsv = () => {
    const target = selectedRows.length ? selectedRows : rows;
    if (!target.length) { toast.error("Nothing to export"); return; }
    const cols = [
      ["Order ID", (s) => s.order_id],
      ["Tracking Number", (s) => s.tracking_number],
      ["Courier", (s) => s.courier_partner],
      ["Payment", (s) => (s.payment_method === "cod" ? "COD" : s.payment_method === "prepaid" ? "Prepaid" : "")],
      ["Customer Phone", (s) => s.phone],
      ["City", (s) => s.city],
      ["State", (s) => s.state],
      ["Country", (s) => s.country],
      ["Zip", (s) => s.zip_code],
      ["Direction", (s) => (s.direction === "return" ? "Return" : "Forward")],
      ["Status", (s) => stageMap?.[s.stage || "open"]?.label || s.stage || "Open"],
      ["Final action required", (s) => metaMap?.[s.status]?.label || s.status || ""],
      ["Assigned to", (s) => s.assigned_to_name || "Unassigned"],
      ["Pickup date", (s) => fmtDate(s.pickup_date)],
      ["Initial EDD", (s) => fmtDate(s.initial_edd)],
      ["TAT est. delivery", (s) => fmtDate(s.tat_estimated_date)],
      ["TAT zone", (s) => s.tat_zone],
      ["Resolution deadline", (s) => fmtDate(s.expected_delivery_date || s.resolution_date)],
      ["Overdue", (s) => (s.is_overdue ? `Yes (${s.overdue_basis || "default"})` : s.is_aging ? "Aging" : "No")],
      ["Tracking link", (s) => s.tracking_link],
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.map((c) => esc(c[0])).join(",")];
    for (const s of target) lines.push(cols.map((c) => esc(c[1](s))).join(","));
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `girorganic-shipments-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${target.length} shipment${target.length === 1 ? "" : "s"} to CSV`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-[#111411] tracking-tight">NDR Shipments</h1>
          <p className="text-muted-foreground mt-1" data-testid="count-split">
            {rows.length} shipments · {rows.filter((r) => (r.direction || "forward") === "forward").length} forward · {rows.filter((r) => r.direction === "return").length} return · {overdueCount} overdue
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="open-upload-button" onClick={() => setUploadOpen(true)} className="gap-2">
            <Upload className="h-4 w-4" /> Import
          </Button>
          <Button data-testid="add-shipment-button" onClick={() => { setEditing(null); setFormOpen(true); }}
                  className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
            <Plus className="h-4 w-4" /> Add shipment
          </Button>
        </div>
      </div>

      <div className="inline-flex items-center rounded-lg border border-border bg-white p-1 gap-1" data-testid="direction-tabs">
        {[
          { key: "all", label: "All" },
          { key: "forward", label: "Forward" },
          { key: "return", label: "Return (RTO)" },
        ].map((t) => (
          <button key={t.key} type="button" data-testid={`direction-tab-${t.key}`}
                  onClick={() => setDirection(t.key)}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    direction === t.key ? "bg-[#0B3D3D] text-white" : "text-muted-foreground hover:text-[#111411]"
                  }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search order ID, tracking, city…" className="pl-9" />
        </div>
        <MultiSelect allLabel="All final actions" testid="filter-status" width="w-[190px]"
          options={statuses.map((s) => ({ value: s.key, label: s.label }))}
          value={status} onChange={setStatus} />
        <MultiSelect allLabel="All statuses" testid="filter-stage" width="w-[150px]"
          options={stages.map((s) => ({ value: s.key, label: s.label }))}
          value={stage} onChange={setStage} />
        <MultiSelect allLabel="All couriers" testid="filter-courier" width="w-[170px]"
          options={couriers.map((c) => ({ value: c, label: c }))}
          value={courier} onChange={setCourier} />
        <MultiSelect allLabel="All payments" testid="filter-payment" width="w-[150px]"
          options={[{ value: "prepaid", label: "Prepaid" }, { value: "cod", label: "COD" }]}
          value={payment} onChange={setPayment} />
        <Button variant={overdue ? "default" : "outline"} data-testid="filter-overdue"
                onClick={() => setOverdue((v) => !v)}
                className={`gap-2 ${overdue ? "bg-[#C24127] hover:bg-[#a5361f] text-white" : ""}`}>
          <AlertTriangle className="h-4 w-4" /> Overdue
        </Button>
        <Button variant={unresolved ? "default" : "outline"} data-testid="filter-unresolved"
                onClick={() => setUnresolved((v) => !v)}
                className={`gap-2 ${unresolved ? "bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white" : ""}`}>
          <CircleSlash className="h-4 w-4" /> Unresolved
        </Button>
        <Button variant={mineOnly ? "default" : "outline"} data-testid="filter-mine"
                onClick={() => setMineOnly((v) => !v)}
                className={`gap-2 ${mineOnly ? "bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white" : ""}`}>
          <Users2 className="h-4 w-4" /> Assigned to me
        </Button>
        <SavedViews page="shipments"
          filters={{ status, stage, courier, overdue, unresolved, search, mineOnly }}
          fields={[
            { key: "search", label: "Search text", type: "text", placeholder: "Order ID, tracking, city…" },
            { key: "status", label: "Final actions", type: "multiselect", options: statuses.map((s) => ({ value: s.key, label: s.label })) },
            { key: "stage", label: "Statuses", type: "multiselect", options: stages.map((s) => ({ value: s.key, label: s.label })) },
            { key: "courier", label: "Couriers", type: "multiselect", options: couriers.map((c) => ({ value: c, label: c })) },
            { key: "overdue", label: "Only overdue", type: "boolean" },
            { key: "unresolved", label: "Only unresolved", type: "boolean" },
            { key: "mineOnly", label: "Assigned to me", type: "boolean" },
          ]}
          onApply={(f) => {
            setStatus(Array.isArray(f.status) ? f.status : []);
            setStage(Array.isArray(f.stage) ? f.stage : []);
            setCourier(Array.isArray(f.courier) ? f.courier : []);
            setOverdue(!!f.overdue);
            setUnresolved(!!f.unresolved);
            setSearch(f.search ?? "");
            setMineOnly(!!f.mineOnly);
          }} />
        <Button onClick={whatsapp} data-testid="whatsapp-button" className="gap-2 bg-[#1FA855] hover:bg-[#178a45] text-white">
          <MessageCircle className="h-4 w-4" /> WhatsApp {selectedRows.length ? `(${selectedRows.length})` : "all"}
        </Button>
        <Button onClick={exportCsv} data-testid="export-csv-button" variant="outline" className="gap-2">
          <Download className="h-4 w-4" /> Export CSV {selectedRows.length ? `(${selectedRows.length})` : ""}
        </Button>
        {selectedRows.length > 0 && (
          <Button onClick={() => setBulkOpen(true)} data-testid="bulk-followup-button" variant="outline" className="gap-2 border-[#1FA855] text-[#178a45]">
            <Users2 className="h-4 w-4" /> Bulk follow-up ({selectedRows.length})
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-border bg-white overflow-x-auto">
        <Table className="min-w-[1000px]">
          <TableHeader>
            <TableRow className="bg-[#F4F6F4]">
              <TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all" /></TableHead>
              <TableHead>Order ID</TableHead>
              <TableHead>Courier / Tracking</TableHead>
              <TableHead>Destination</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Initial EDD</TableHead>
              <TableHead>TAT est. delivery</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Final action required</TableHead>
              <TableHead>Assigned to</TableHead>
              <TableHead>Resolution deadline</TableHead>
              <TableHead>Track</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={13} className="text-center py-12 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (              <TableRow><TableCell colSpan={13} className="text-center py-16">
                <img src="https://images.unsplash.com/photo-1577705998148-6da4f3963bc8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxjYXJkYm9hcmQlMjBwYWNrYWdlJTIwZGVsaXZlcnklMjBib3h8ZW58MHx8fHwxNzg2NzcwMTM0fDA&ixlib=rb-4.1.0&q=85"
                     alt="No shipments" className="h-32 w-32 object-cover rounded-lg mx-auto mb-4 opacity-90" />
                <p className="text-muted-foreground">No shipments found. Add one or import a ClickPost export.</p>
              </TableCell></TableRow>
            ) : pagedRows.map((s) => (
              <TableRow key={s.id} data-testid={`shipment-row-${s.id}`}
                        className={s.is_overdue ? "border-l-4 border-l-[#C24127]" : ""}>
                <TableCell><Checkbox checked={!!selected[s.id]} onCheckedChange={() => toggle(s.id)} data-testid={`check-${s.id}`} /></TableCell>
                <TableCell className="font-medium text-[#111411] align-top">
                  <div className="flex flex-col gap-1.5 py-0.5">
                    <Link to={`/shipments/${s.id}`} data-testid={`order-link-${s.id}`} className="text-[#0B3D3D] hover:underline w-fit">
                      {s.order_id}
                    </Link>
                    {s.phone && (
                      <a href={`https://wa.me/${String(s.phone).replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                         data-testid={`phone-${s.id}`}
                         className="text-xs text-muted-foreground hover:text-[#1FA855] flex items-center gap-1 w-fit">
                        <Phone className="h-3 w-3" /> {s.phone}
                      </a>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5">
                        <DirectionBadge direction={s.direction} />
                        {s.attempt_total > 1 && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8A6D1F] bg-[#FBEFD9] rounded-full px-2 py-0.5 whitespace-nowrap"
                                data-testid={`shipment-count-chip-${s.id}`}
                                title={`This order has ${s.attempt_total} shipments sent`}>
                            <RefreshCw className="h-3 w-3 shrink-0" /> Shipment {s.attempt_index} of {s.attempt_total}
                          </span>
                        )}
                        {s.reshipped_from && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#0B3D3D] bg-[#E8F0EC] rounded-full px-2 py-0.5 whitespace-nowrap"
                                data-testid={`reship-tag-${s.id}`}>
                            <RefreshCw className="h-3 w-3 shrink-0" /> replaces #{s.reshipped_from_order || "—"}
                          </span>
                        )}
                      </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm font-medium">{s.courier_partner}</div>
                  <div className="text-xs text-muted-foreground">{s.tracking_number}</div>
                </TableCell>
                <TableCell className="text-sm">
                  {[s.city, s.state].filter(Boolean).join(", ") || "—"}
                  <div className="text-xs text-muted-foreground">{s.zip_code}</div>
                </TableCell>
                <TableCell data-testid={`payment-${s.id}`}><PaymentBadge method={s.payment_method} /></TableCell>
                <TableCell className="text-sm" data-testid={`edd-${s.id}`}>{fmtDate(s.initial_edd)}</TableCell>
                <TableCell className="text-sm" data-testid={`tat-${s.id}`}>
                  {s.tat_estimated_date ? fmtDate(s.tat_estimated_date) : <span className="text-muted-foreground">—</span>}
                  {s.tat_zone && <div className="text-xs text-[#0B3D3D] font-medium" data-testid={`zone-${s.id}`}>{s.tat_zone}</div>}
                </TableCell>
                <TableCell>
                  <Select value={s.stage || "open"} onValueChange={(v) => changeStage(s.id, v)}>
                    <SelectTrigger className="h-8 w-[130px] border-none shadow-none p-0 hover:bg-transparent" data-testid={`stage-select-${s.id}`}>
                      <StageBadge stage={s.stage || "open"} />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map((st) => <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select value={s.status} onValueChange={(v) => changeStatus(s.id, v)}>
                    <SelectTrigger className="h-8 w-[180px] border-none shadow-none p-0 hover:bg-transparent" data-testid={`status-select-${s.id}`}>
                      <StatusBadge status={s.status} />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map((st) => <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-[#111411]" data-testid={`assigned-${s.id}`}>
                    {s.assigned_to_name || <span className="text-muted-foreground">Unassigned</span>}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`text-sm ${s.is_overdue ? "text-[#C24127] font-semibold" : "text-[#111411]"}`}
                        data-testid={`resolution-${s.id}`}>
                    {fmtDate(s.expected_delivery_date || s.resolution_date)}
                  </span>
                  {s.is_overdue ? (
                    <div className="text-xs text-[#C24127] font-medium" data-testid={`overdue-${s.id}`}
                         title={s.overdue_basis === "edd" ? "Past initial EDD" : s.overdue_basis === "tat" ? "Past courier TAT" : "Past 7-day window"}>
                      Overdue · {s.overdue_basis === "edd" ? "EDD" : s.overdue_basis === "tat" ? "TAT" : "default"}
                    </div>
                  ) : s.is_aging ? (
                    <div className="text-xs text-[#B45309] font-medium" data-testid={`aging-${s.id}`}>Aging &gt;7d</div>
                  ) : null}
                </TableCell>
                <TableCell>
                  {s.tracking_link ? (
                    <a href={s.tracking_link} target="_blank" rel="noreferrer"
                       data-testid={`track-link-${s.id}`}
                       className="inline-flex items-center gap-1 text-sm text-[#0B3D3D] font-medium hover:underline">
                      Track <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`row-menu-${s.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem data-testid={`edit-${s.id}`} onClick={() => { setEditing(s); setFormOpen(true); }}>
                        <Pencil className="h-4 w-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`reship-${s.id}`} onClick={() => setReshipFor(s)}>
                        <RefreshCw className="h-4 w-4 mr-2" /> Create reshipment
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`rvp-${s.id}`} onClick={() => setRvpFor(s)}>
                        <Undo2 className="h-4 w-4 mr-2" /> Mark reverse pickup
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`history-${s.id}`} onClick={() => setHistoryFor(s)}>
                        <History className="h-4 w-4 mr-2" /> View history
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`wa-${s.id}`} onClick={() => setWaPrompt([s])}>
                        <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp follow-up
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`delete-${s.id}`} onClick={() => remove(s.id)} className="text-[#C24127]">
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {rows.length > 0 && (
        <div className="flex items-center justify-between gap-4 flex-wrap px-1" data-testid="pagination-bar">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Rows per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="h-8 w-[74px]" data-testid="page-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground" data-testid="page-range">
              {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, rows.length)} of {rows.length}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)} data-testid="page-prev">Prev</Button>
              <span className="px-2 text-sm font-medium" data-testid="page-indicator">{currentPage} / {pageCount}</span>
              <Button variant="outline" size="sm" disabled={currentPage >= pageCount} onClick={() => setPage(currentPage + 1)} data-testid="page-next">Next</Button>
            </div>
          </div>
        </div>
      )}
      <ShipmentForm open={formOpen} onOpenChange={setFormOpen} editing={editing} agents={agents} onSaved={load} />
      <ReshipDialog shipment={reshipFor} onClose={() => setReshipFor(null)} onDone={() => { load(); loadPending(); refreshReship(); if (chainRvpFor) { const r = chainRvpFor; setChainRvpFor(null); setRvpFor(r); } }} />
      <ReversePickupDialog open={!!rvpFor} onOpenChange={(o) => !o && setRvpFor(null)} existing={rvpFor}
                           onDone={() => { setRvpFor(null); load(); refreshRvp(); if (chainReshipFor) { const r = chainReshipFor; setChainReshipFor(null); setReshipFor(r); } }} />
      <TriggerChooserDialog open={!!chooserFor} onOpenChange={(o) => !o && setChooserFor(null)} orderId={chooserFor?.order_id} onChoose={handleChooseRow} />
      <LinkedCleanupDialog open={!!cleanupFor} onOpenChange={(o) => !o && setCleanupFor(null)} orderId={cleanupFor?.orderId}
                           dropReship={cleanupFor?.dropReship} dropRvp={cleanupFor?.dropRvp} onDecision={confirmCleanupRow} />
      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onDone={() => { setUploadOpen(false); load(); }} />
      <HistoryDialog shipment={historyFor} onClose={() => setHistoryFor(null)} />
      <BulkFollowupDialog open={bulkOpen} onOpenChange={setBulkOpen} shipments={selectedRows}
                          onPick={(items) => { setBulkOpen(false); setWaPrompt(items); }} />
      <WhatsAppPromptDialog shipments={waPrompt} onClose={() => setWaPrompt(null)} />
    </div>
  );
}


function UploadDialog({ open, onOpenChange, onDone }) {
  const localRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/import-batches").then((r) => setHistory(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { if (open) { loadHistory(); } }, [open]);

  const undoBatch = async (b) => {
    if (!window.confirm(`Undo import of "${b.filename || "file"}"? This removes ${b.remaining} shipment(s).`)) return;
    try {
      const r = await api.delete(`/shipments/import-batch/${b.batch_id}`);
      toast.success(`Undone — ${r.data.deleted} shipment(s) removed`);
      loadHistory();
      onDone?.();
    } catch { toast.error("Could not undo import"); }
  };

  const reset = () => { setFile(null); setPreview(null); if (localRef.current) localRef.current.value = ""; };

  const downloadSample = async () => {
    try {
      const res = await api.get("/shipments/sample-file", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "girorganic_ndr_sample.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download sample file");
    }
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setLoading(true); setPreview(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/shipments/upload?dry_run=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not read file");
      reset();
    } finally { setLoading(false); }
  };

  const confirmImport = async () => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/shipments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} shipments imported${data.errors.length ? `, ${data.errors.length} skipped` : ""}`, {
        duration: 10000,
        action: (data.batch_id && data.created > 0) ? {
          label: "Undo",
          onClick: async () => {
            try {
              const r = await api.delete(`/shipments/import-batch/${data.batch_id}`);
              toast.success(`Undone — ${r.data.deleted} shipment(s) removed`);
              onDone?.();
            } catch { toast.error("Could not undo import"); }
          },
        } : undefined,
      });
      reset();
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setImporting(false); }
  };

  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="upload-dialog" className={preview ? "max-w-4xl max-h-[88vh] overflow-y-auto" : ""}>
        <DialogHeader><DialogTitle className="font-heading">Import shipments</DialogTitle></DialogHeader>

        {!preview && (
          <>
            <p className="text-sm text-muted-foreground">
              Upload a CSV or Excel export. Recognised columns: <span className="font-medium">order_id, tracking_number,
              courier_partner, country, zip_code, pickup_date, initial_edd, phone, shipment_status, final_action, assigned_to, notes</span>.
              City &amp; State fill automatically from the PIN — no need to add them.
              You&apos;ll see a preview before anything is imported.
            </p>
            <div className="flex items-center justify-between rounded-md border border-border bg-[#F9F9F8] p-3">
              <div className="flex items-center gap-2 text-sm text-[#111411]">
                <FileSpreadsheet className="h-4 w-4 text-[#0B3D3D]" /> Sample Excel template (with dropdowns)
              </div>
              <Button variant="outline" size="sm" onClick={downloadSample} data-testid="download-sample-button" className="gap-2">
                <Download className="h-4 w-4" /> Download sample
              </Button>
            </div>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center bg-[#F9F9F8]">
              {loading ? (
                <div className="flex items-center justify-center text-muted-foreground" data-testid="upload-reading">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Reading file…
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-[#0B3D3D] mx-auto mb-2" />
                  <input ref={localRef} type="file" accept=".csv,.xlsx,.xls" onChange={onPick}
                         data-testid="upload-file-input" className="hidden" id="upload-input" />
                  <Button onClick={() => localRef.current?.click()} data-testid="choose-file-button"
                          className="bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">Choose file</Button>
                </>
              )}
            </div>
            {history.length > 0 && (
              <div className="space-y-2" data-testid="import-history">
                <p className="text-xs font-medium text-muted-foreground">Recent imports</p>
                <div className="border border-border rounded-lg divide-y divide-border max-h-52 overflow-auto">
                  {history.map((b) => (
                    <div key={b.batch_id} data-testid={`history-row-${b.batch_id}`}
                         className="flex items-center gap-3 px-3 py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[#111411] truncate">{b.filename || "Imported file"}</p>
                        <p className="text-xs text-muted-foreground">
                          {fmtDate(b.created_at)} · {b.created} imported · by {b.created_by_name || "—"}
                        </p>
                      </div>
                      {b.undone ? (
                        <span className="text-xs text-muted-foreground">Undone</span>
                      ) : b.remaining === 0 ? (
                        <span className="text-xs text-muted-foreground">Removed</span>
                      ) : (
                        <Button size="sm" variant="outline" data-testid={`history-undo-${b.batch_id}`}
                                onClick={() => undoBatch(b)} className="gap-1.5 text-[#C24127] border-[#EBC7BE]">
                          <Trash2 className="h-3.5 w-3.5" /> Undo
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {preview && (
          <div className="space-y-3" data-testid="import-preview">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-[#1E7A34] font-semibold" data-testid="preview-valid">{preview.valid} ready to import</span>
              {preview.invalid > 0 && <span className="text-[#C24127] font-semibold" data-testid="preview-invalid">{preview.invalid} with errors</span>}
              {preview.duplicates > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#FBEFD9] text-[#8A6D1F] font-semibold px-2.5 py-0.5"
                      data-testid="preview-duplicates">
                  {preview.duplicates} duplicate{preview.duplicates === 1 ? "" : "s"} skipped
                </span>
              )}
              <span className="text-muted-foreground">of {preview.total_rows} rows</span>
            </div>
            <div className="border border-border rounded-lg overflow-auto max-h-[46vh]">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F6F5] sticky top-0">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">Row</th><th className="px-3 py-2">Order</th><th className="px-3 py-2">Tracking</th>
                    <th className="px-3 py-2">Courier</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Final action</th>
                    <th className="px-3 py-2">Assigned</th><th className="px-3 py-2">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((p) => (
                    <tr key={p.row} data-testid={`preview-row-${p.row}`}
                        className={`border-t border-border ${p.valid ? "" : "bg-[#FCEEEA]"}`}>
                      <td className="px-3 py-2 text-muted-foreground">{p.row}</td>
                      <td className="px-3 py-2 font-medium text-[#111411]">{p.order_id || "—"}</td>
                      <td className="px-3 py-2">{p.tracking_number || "—"}</td>
                      <td className="px-3 py-2">{p.courier_partner || "—"}</td>
                      <td className="px-3 py-2">{p.shipment_status}</td>
                      <td className="px-3 py-2">{p.final_action}</td>
                      <td className="px-3 py-2">{p.assigned_to}</td>
                      <td className="px-3 py-2">{p.valid
                        ? <span className="text-[#1E7A34] font-medium">Ready</span>
                        : <span className="text-[#C24127]">{p.error}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.invalid > 0 && (
              <p className="text-xs text-muted-foreground">Rows with errors will be skipped. Fix them in your file and re-upload to include them.</p>
            )}
          </div>
        )}

        {preview && (
          <DialogFooter>
            <Button variant="outline" onClick={reset} data-testid="preview-back">Choose another file</Button>
            <Button onClick={confirmImport} disabled={importing || preview.valid === 0} data-testid="preview-confirm"
                    className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
              {importing && <Loader2 className="h-4 w-4 animate-spin" />} Import {preview.valid} shipment{preview.valid === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}


function HistoryDialog({ shipment, onClose }) {
  return (
    <Dialog open={!!shipment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="history-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Status history {shipment ? `· ${shipment.order_id}` : ""}
          </DialogTitle>
        </DialogHeader>
        {shipment && (
          <div className="text-sm text-muted-foreground mb-3">
            AWB {shipment.tracking_number} · {shipment.courier_partner}
          </div>
        )}
        <StatusTimeline history={shipment?.status_history} />
      </DialogContent>
    </Dialog>
  );
}


function BulkFollowupDialog({ open, onOpenChange, shipments, onPick }) {
  const groups = useMemo(() => groupByCourier(shipments || []), [shipments]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-followup-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Bulk follow-up</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          Your {shipments?.length || 0} selected shipment(s) are grouped by courier partner below. Send a ready-made
          WhatsApp message to each courier — one tap per partner.
        </p>
        <div className="space-y-3 py-2">
          {groups.map((g) => (
            <div key={g.courier} data-testid={`bulk-group-${g.courier}`}
                 className="flex items-center justify-between gap-3 border border-border rounded-md p-3">
              <div>
                <p className="text-sm font-medium text-[#111411]">{g.courier}</p>
                <p className="text-xs text-muted-foreground">
                  {g.items.length} shipment(s) · {g.items.map((i) => i.tracking_number).slice(0, 3).join(", ")}
                  {g.items.length > 3 ? "…" : ""}
                </p>
              </div>
              <Button size="sm" data-testid={`bulk-wa-${g.courier}`}
                      onClick={() => onPick(g.items)}
                      className="gap-2 bg-[#1FA855] hover:bg-[#178a45] text-white shrink-0">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </Button>
            </div>
          ))}
          {groups.length === 0 && <p className="text-sm text-muted-foreground">No shipments selected.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WhatsAppPromptDialog({ shipments, onClose }) {
  const { statuses } = useStatuses();
  const [statusKey, setStatusKey] = useState("");
  const [templates, setTemplates] = useState({});
  useEffect(() => {
    if (shipments) {
      api.get("/settings").then((r) => setTemplates(r.data.wa_templates || {})).catch(() => {});
      setStatusKey((prev) => prev || statuses[0]?.key || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipments]);

  const statusLabel = statuses.find((s) => s.key === statusKey)?.label || statusKey;
  const template = templates[statusKey] || templates.default || "";
  const preview = shipments ? buildWaMessage(shipments, { statusLabel, template }) : "";
  const send = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(preview)}`, "_blank");
    onClose();
  };
  return (
    <Dialog open={!!shipments} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="wa-prompt-dialog" className="max-h-[85vh] flex flex-col">
        <DialogHeader><DialogTitle className="font-heading">WhatsApp follow-up</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          {shipments?.length || 0} shipment(s) selected. Pick the final action — the message uses your saved
          template (Settings → WhatsApp templates) when available.
        </p>
        <div className="py-2 space-y-3 overflow-y-auto flex-1 min-h-0">
          <div>
            <Label>Final action required</Label>
            <Select value={statusKey} onValueChange={setStatusKey}>
              <SelectTrigger data-testid="wa-status" className="mt-1.5"><SelectValue placeholder="Select final action" /></SelectTrigger>
              <SelectContent>
                {statuses.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Message preview</Label>
            <pre data-testid="wa-preview" className="mt-1.5 max-h-48 overflow-y-auto text-xs text-muted-foreground whitespace-pre-wrap break-all bg-[#F9F9F8] border border-border rounded-md p-3">
              {preview}
            </pre>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} data-testid="wa-send" className="gap-2 bg-[#1FA855] hover:bg-[#178a45] text-white">
            <MessageCircle className="h-4 w-4" /> Open WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

